import { Injectable } from '@angular/core';
import { ApiService } from '../../common/services/api.service';
import { BehaviorSubject, catchError, finalize, Observable, of, shareReplay, throwError } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { JsendResponse } from '../../common/types/jsend.response.type';

@Injectable({
    providedIn: 'root',
})

/**
 * Layout Service
 *
 * @export
 * @class LayoutService
 */
export class LayoutService {
    /**
     * Per-path cache of page-layout responses. Keyed by the requested page path so
     * each page keeps its own cached layout. A single-entry cache (one path at a
     * time) meant that visiting a second page evicted the first; on back-navigation
     * the first page then had to be re-fetched, and because the server returns fresh
     * section uuids per fetch, the page-builder's `@for (... ; track section.uuid)`
     * tore down and rebuilt the listing, firing a redundant property search. Caching
     * per path keeps e.g. the homes layout warm so returning to it is a cache hit
     * with identical uuids — no rebuild, no redundant search.
     */
    private layoutCache = new Map<string, Observable<HttpResponse<JsendResponse>>>();
    static loader: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * Creates an instance of APIService.
     *
     * @param {ApiService} apiService
     *
     * @memberof VendorService
     */
    constructor(private apiService: ApiService) {}

    /**
     * Get website layout
     *
     * @returns Observable<HttpResponse<JsendResponse>>
     */
    public getWebsiteLayout(url: string): Observable<HttpResponse<JsendResponse>> {
        return this.apiService.get(`api/v1/global-settings/website-layout`, { url });
    }

    /**
     * Fetches whether closed/sold property listings are enabled for the current domain.
     * @returns Observable with closed_properties_enabled boolean in the response body.
     */
    public getClosedPropertiesStatus(): Observable<HttpResponse<JsendResponse>> {
        return this.apiService.get('api/v1/global-settings/closed-properties');
    }

    /**
     * Get home page
     *
     * @returns Observable<HttpResponse<JsendResponse>>
     */
    public getHomePageLayout(): Observable<HttpResponse<JsendResponse>> {
        return this.apiService.get(`api/v1/pages`);
    }

    /**
     * Get all properties
     *
     * @returns Observable<HttpResponse<JsendResponse>>
     */
    public getPageLayout(path: string): Observable<HttpResponse<JsendResponse>> {
        const cached = this.layoutCache.get(path);
        if (cached) {
            // Cache hit — the layout is already available, so there is nothing to load.
            // (Don't flip the loader on here: the cached shareReplay'd observable's
            // finalize won't run again to turn it back off, which would leave the global
            // loading indicator stuck on for revisited pages.)
            return cached;
        }
        LayoutService.loader.next(true);
        const response$ = this.apiService.get(`api/v1/pages`, { url: path }).pipe(
            finalize(() => LayoutService.loader.next(false)),
            catchError((error) => {
                // Don't cache a failed response — drop it so the next request retries.
                this.layoutCache.delete(path);
                return throwError(() => error);
            }),
            // Share the response and replay the last emitted value to future subscribers
            shareReplay(1),
        );
        // Cache synchronously and per path, so requesting a different page never evicts
        // this one (which previously forced a re-fetch — and a redundant search — on
        // back-navigation).
        this.layoutCache.set(path, response$);

        return response$;
    }

    /**
     * Get home page
     *
     * @returns Observable<HttpResponse<JsendResponse>>
     */
    public getBlogHomeLayout(page: string): Observable<HttpResponse<JsendResponse>> {
        return this.apiService.get(`api/v1/pages`, {
            url: page,
        });
    }
}
